import React, { useState } from 'react';
import { Globe, ChevronDown, ChevronRight, X } from 'lucide-react';

const BRAND_BLUE = '#2B4B6A';

/**
 * Collapsible "Records per Country" panel — ranked country counts with bars,
 * top 12 by default and a toggle to expand to the full list. Clicking a country
 * selects it (clicking the selected one clears).
 *
 * Shared by the admin data view (/admin/cd) and the public directory (/directory)
 * so the two can never drift apart.
 *
 * Props:
 *   countries  [{ country, count }] — caller supplies the order it wants (usually count desc)
 *   selected   currently selected country ('' or null when none)
 *   onSelect   (country) => void — receives '' when the selection is cleared
 *   defaultOpen  start expanded (default true)
 *   title      heading text
 */
const DirectoryCountryPanel = ({
  countries = [],
  selected = '',
  onSelect,
  defaultOpen = true,
  title = 'Records per Country',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? countries : countries.slice(0, 12);
  const max = countries[0]?.count || 1;

  const choose = (country) => {
    if (!onSelect) return;
    onSelect(selected === country ? '' : country);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="country-panel-toggle"
        className="w-full flex items-center justify-between px-5 py-3"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <Globe className="w-4 h-4" style={{ color: BRAND_BLUE }} />
          {title}
          <span className="font-normal text-gray-400">({countries.length} countries)</span>
        </span>
        {selected && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onSelect?.(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onSelect?.(''); } }}
            className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700 cursor-pointer"
          >
            {selected} <X className="w-3 h-3" />
          </span>
        )}
      </button>

      {open && (
        <div className="px-5 pb-4">
          {countries.length === 0 ? (
            <p className="text-sm text-gray-400 py-3">No data</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-1.5">
                {visible.map(({ country, count }) => (
                  <button
                    key={country}
                    type="button"
                    onClick={() => choose(country)}
                    className={`flex items-center gap-2 text-left px-2 py-1 rounded-md hover:bg-gray-50 ${
                      selected === country ? 'bg-blue-50' : ''
                    }`}
                  >
                    <span className="text-xs text-gray-700 w-28 truncate" title={country}>{country}</span>
                    <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <span
                        className="block h-full rounded-full"
                        style={{
                          width: `${Math.max((count / max) * 100, 3)}%`,
                          backgroundColor: selected === country ? BRAND_BLUE : '#93b4cf',
                        }}
                      />
                    </span>
                    <span className="text-xs font-semibold text-gray-900 w-10 text-right">{count}</span>
                  </button>
                ))}
              </div>
              {countries.length > 12 && (
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  data-testid="country-panel-showall"
                  className="mt-3 text-xs font-medium hover:underline"
                  style={{ color: BRAND_BLUE }}
                >
                  {showAll ? 'Show top 12' : `Show all ${countries.length} countries`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DirectoryCountryPanel;
